import React, {useEffect, useState} from 'react';
import cn from 'classnames';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import {
  doesSpeakerMatchFilter,
  togglePill,
  TOPIC_CONTRAST_TEXT,
} from '@components/call-card/TopicKeyword.utils';
import './KeywordTopicSidebar.sass';
import {SearchIcon} from '@components/icon/SearchIcon';
import {Matchable, Topic, TopicId, TopicFilter} from '@store/topic-keyword/TopicKeyword.types';
import {List, Map, Set} from 'immutable';
import {Color, Int} from '@store/@types/util';
import throttle from 'lodash/throttle';
import {makeStyles} from '@material-ui/core/styles';
import {CallCardSource} from '@store/call-card/CallCardSource';
import {KeywordSearchResult, Speaker, SpeakerNumber} from '@store/call-card/CallCard.types';
import {SubtleDropdown} from 'charts/components/forms/SubtleDropdown';
import CallCardActions from '@store/call-card/CallCardActions';
import {FilterWordDisplayWhen} from 'records/admin/topics/NewFilterWord';

interface KeywordTopicSidebarProps {
  callId: FileUploadId;
  keywordSearch: typeof CallCardSource.keywordSearch;
  // Topics related to this call.
  topics: List<Topic>;
  // Topics which are displayed on the call card
  activeTopics: Map<TopicId, Color>;
  // Should the sidebar be collapsed or shown
  hidden: boolean;
  speakers: List<Speaker>;
  topicFilter: TopicFilter;
}

export const KeywordTopicSidebar: React.FC<KeywordTopicSidebarProps> = ({
  callId,
  keywordSearch,
  topics,
  activeTopics,
  hidden,
  speakers,
  topicFilter,
}) => {
  const [search, setSearch] = useState('');
  const [keywords, setKeywords] = useState<KeywordSearchResult[]>([]);
  const [options, setOptions] = useState<KeywordSearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setTimeout(async () => {
      try {
        const results = await keywordSearch(callId, search);
        const uniqueResults = dedupeResults(results);
        if (search === '') {
          // Only show default topics as options on empty search
          const topicSet = Set(topics.map(t => t.id));
          setOptions(uniqueResults.filter(r => topicSet.has(r.id)));
        } else {
          setOptions(uniqueResults);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }, 0);
  }, [search, callId, keywordSearch, topics]);

  const [filteredKeywords, filteredTopics] = filterKeywordsAndTopics(
    List(keywords),
    topics,
    topicFilter,
    speakers
  );

  const keywordSet = Set(keywords.map(k => k.id));

  const autocompleteClasses = useStyles();

  return (
    <div className={cn('keyword-topic-sidebar', {hidden})}>
      <label className="search-label">Search to add Keywords or Topics</label>
      <Autocomplete
        size="small"
        popupIcon={<SearchIcon tooltip="Search" />}
        classes={autocompleteClasses}
        getOptionLabel={(option: KeywordSearchResult) => `${option.word} (${option.occurrences})`}
        onInputChange={(event, value) => {
          // Ignore these types of events. They set incorrect data
          if (!event || event.type === 'click' || event.type === 'blur') return;
          setSearch(value);
        }}
        inputValue={search}
        fullWidth
        renderTags={() => null}
        disableClearable
        getOptionDisabled={option => keywordSet.has(option.id) || activeTopics.has(option.topicId)}
        onChange={(_, newKeyword) => {
          // Find the new keyword/topic, then toggle it on
          togglePill(callId, newKeyword, false);

          // Add this new value to keywords only if we don't find it in topics.
          if (topics.every(t => t.id !== newKeyword.id)) {
            setKeywords([...keywords, newKeyword]);
          }
        }}
        renderInput={params => (
          <TextField
            {...params}
            label=""
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        options={loading ? [] : options}
      />
      <div className="filter-dropdown">
        <SubtleDropdown
          options={[
            {id: TopicFilter.EITHER, name: 'display internal & external'},
            {id: TopicFilter.INTERNAL, name: 'display internal only'},
            {id: TopicFilter.EXTERNAL, name: 'display external only'},
          ]}
          selected={topicFilter}
          onSelect={optionId => CallCardActions.setTopicFilter(callId, optionId as TopicFilter)}
        />
      </div>
      <div className="topics">
        {filteredKeywords.map(v => (
          <TopicPill key={v.id} callId={callId} topic={v} color={activeTopics.get(v.id)} />
        ))}
        {filteredTopics.map(v => (
          <TopicPill key={v.id} callId={callId} topic={v} color={activeTopics.get(v.id)} />
        ))}
      </div>
    </div>
  );
};

interface TopicPillInterface {
  callId: FileUploadId;
  topic: Matchable;
  color: Color | undefined;
}

function TopicPill({callId, topic, color}: TopicPillInterface) {
  const name = 'name' in topic ? topic.name : topic.word;
  const numOccurrences =
    typeof topic.occurrences === 'number' ? topic.occurrences : topic.occurrences.size;
  return (
    <div
      onClick={
        numOccurrences > 0 ? throttle(() => togglePill(callId, topic, !!color), 1000) : undefined
      }
      style={
        numOccurrences > 0 && color
          ? {backgroundColor: color, color: TOPIC_CONTRAST_TEXT[color]}
          : {}
      }
      className="topic-pill">
      {name} ({numOccurrences})
    </div>
  );
}

const useStyles = makeStyles(() => ({
  popupIndicatorOpen: {
    transform: 'none',
  },
}));

// Uses an old API that returns separate results for old speaker types.
// Combine them into one result, and sort by occurrences.
function dedupeResults(results: List<KeywordSearchResult> | undefined): KeywordSearchResult[] {
  if (!results) return [];
  const combinedResults = results.reduce((acc, val) => {
    return acc.update(val.id, result => {
      if (result) {
        return {
          ...result,
          occurrences: val.occurrences + result.occurrences,
          occurrencesBySpeaker: result.occurrencesBySpeaker!.update(
            val.speakerNumber,
            occ => (occ ?? 0) + val.occurrences
          ),
        };
      } else {
        val.occurrencesBySpeaker = Map<SpeakerNumber, Int>().update(
          val.speakerNumber,
          () => val.occurrences
        );
        return val;
      }
    });
  }, Map<string, KeywordSearchResult>());

  return combinedResults
    .sortBy(r => r.occurrences)
    .reverse()
    .toArray();
}

function filterKeywordsAndTopics(
  keywords: List<KeywordSearchResult>,
  topics: List<Topic>,
  topicFilter: TopicFilter,
  speakers: List<Speaker>
): [List<KeywordSearchResult>, List<Topic>] {
  // Filter keyword occurrences based on internal/external filter
  const filteredKeywords = keywords.map(keyword => {
    if (topicFilter === TopicFilter.EITHER || !keyword.occurrencesBySpeaker) {
      return keyword;
    }

    let occurrences = 0;
    keyword.occurrencesBySpeaker!.keySeq().forEach(speakerNumber => {
      const speaker = speakers.find(s => s.speakerNumber === speakerNumber);
      if (doesSpeakerMatchFilter(speaker, topicFilter)) {
        occurrences += keyword.occurrencesBySpeaker!.get(speakerNumber)!;
      }
    });

    return {
      ...keyword,
      occurrences,
    };
  });

  const filteredTopics = topics
    // Filter topic occurrences based on internal/external filter
    .map(topic => {
      if (topicFilter === TopicFilter.EITHER) {
        return topic;
      }
      return new Topic({
        id: topic.id,
        name: topic.name,
        color: topic.color,
        occurrences: topic.occurrences.filter(occurrence => {
          const speaker = speakers.find(s => s.speakerNumber === occurrence.speakerNumber);
          return doesSpeakerMatchFilter(speaker, topicFilter);
        }),
        displayWhen: topic.displayWhen,
      });
    })
    // Filter out topics with: no occurrences and display (only) when found
    .filter(
      topic => topic.occurrences.size > 0 || topic.displayWhen !== FilterWordDisplayWhen.WHEN_FOUND
    );

  return [filteredKeywords, filteredTopics];
}
